import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Grid,
  Icon,
  Tooltip,
  TextField,
  Autocomplete,
  Box,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import {
  selectSx,
  autoCompleteProps,
  ISOToDDMMYYYY,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import { STATUS_LHB_DATA } from "components/staticData";
import { showAlert } from "components/CommonComponent/ShowAlert";
import colors from "assets/theme/base/colors";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getTimeSheetList } from "actions/timesheet";
import { timesheetList } from "constants/timesheet";
import { departmentList } from "constants/department";
import { getDepartmentList } from "actions/department";
import { getShiftList } from "actions/shift";
import { shiftList } from "constants/shift";
import { urlEncodedId } from "components/CommonComponent/encodeDecodeFunction";
import { DEFAULT_RECORDS_PER_PAGE } from "components/common/constant";

const StatusBadge = ({ status }) => {
  if (!status) return null;

  const s = status.toUpperCase();

  const map = {
    PENDING: { color: "secondary", label: "Pending" },
    APPROVED: { color: "success", label: "Approved" },
    REJECTED: { color: "error", label: "Rejected" },
  };

  const config = map[s] || { color: "secondary", label: s };

  return (
    <MDBadge
      badgeContent={config.label}
      color={config.color}
      variant="gradient"
      size="sm"
      circular
    />
  );
};
StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
};

const FILTER_STORAGE_KEY = "lhb_records_filters";

const initialFilters = {
  lhbNo: "",
  empName: "",
  empId: "",
  department: null,
  shift: null,
  status: null,
  fromDate: "",
  toDate: "",
  filterParams: [],
};

function LHBDataListing() {
  const dispatch = useDispatch();
  const timeSheetDataList = useSelector(
    (state) => state.timesheet.timesheetList,
  );

  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );
  const shiftListData = useSelector((state) => state.shift.shiftList);
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState([]);
  // const [pageNum, setPageNum] = useState(1);
  // const [filters, setFilters] = useState({
  //   lhbNo: "",
  //   empName: "",
  //   empId: "",
  //   department: null,
  //   shift: null,
  //   status: null,
  //   fromDate: "",
  //   toDate: "",
  //   filterParams: [],
  // });
  const savedState = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(FILTER_STORAGE_KEY) || "null");
    } catch {
      return null;
    }
  })();

  const [pageNum, setPageNum] = useState(savedState?.pageNum || 1);
  const [filters, setFilters] = useState(savedState?.filters || initialFilters);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  useEffect(() => {
    getTimeSheetList(dispatch, pageNum, filters.filterParams);
    return () => {
      dispatch({ type: timesheetList, payload: {} });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    if (timeSheetDataList?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: timeSheetDataList?.data?.timesheet?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (timeSheetDataList?.code || timeSheetDataList?.message) {
      setListState({
        isLoading: false,
        error:
          timeSheetDataList?.message ||
          "Failed to load timesheet list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [timeSheetDataList]);

  useEffect(() => {
    sessionStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({
        filters,
        pageNum,
      }),
    );
  }, [filters, pageNum]);

  useEffect(() => {
    getDepartmentList(dispatch);
    getShiftList(dispatch);
    return () => {
      dispatch({ type: departmentList, payload: {} });
      dispatch({ type: shiftList, payload: {} });
    };
  }, [dispatch]);

  const formattedListData =
    timeSheetDataList?.data?.timesheet?.map((item, index) => ({
      id: item?.ID,
      date: ISOToDDMMYYYY(item?.shiftStartDate),
      srNo:
        (pageNum - 1) *
          (timeSheetDataList?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,

      lhbNo: `LHB-${item?.lhbNo}`,
      name: item?.technician?.name || "-",
      empNo: item?.technician?.employeeID || "-",
      department: item?.technician?.department?.name || "-",
      shift: item?.shift?.name || "-",
      dutyType: item?.dutyType || "-",
      hours: item?.totalDurationMinutes
        ? (item.totalDurationMinutes / 60).toFixed(1)
        : "0",
      formattedHours: item?.formattedHours || "0.00",
      status: item?.status || "-",
      approvedBy: item?.status === "APPROVED" ? item?.createdBy : "",
      rejectedBy: item?.status === "REJECTED" ? item?.createdBy : "",
      rejectionRemark: item?.rejectionRemark || "-",
    })) || [];

  const openViewModal = (row) => {
    const encodedId = urlEncodedId(row.id);
    navigate(`/admin/lhbData/view/${encodedId}`);
  };

  const departmentOptions =
    departmentListData?.data?.Department?.map((d) => ({
      id: d.ID,
      name: d.name,
      status: d.status,
    })) || [];

  const shiftOptions =
    shiftListData?.data?.shift?.map((s) => ({
      id: s.ID,
      name: s.name,
      startTime: s.startTime,
      endTime: s.endTime,
      status: s.status,
    })) || [];

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.lhbNo) {
      filterParams.push({
        field: "ID",
        condition: "eq",
        filterValues: [filters.lhbNo],
      });
    }

    if (filters.empName) {
      filterParams.push({
        field: "TechnicianName",
        condition: "like",
        filterValues: [String(filters.empName)],
      });
    }

    if (filters.empId) {
      filterParams.push({
        field: "Empid",
        condition: "like",
        filterValues: [String(filters.empId)],
      });
    }

    if (filters.department) {
      filterParams.push({
        field: "TechnicianDeptID",
        condition: "eq",
        filterValues: [String(filters.department)],
      });
    }

    if (filters.shift) {
      filterParams.push({
        field: "ShiftID",
        condition: "eq",
        filterValues: [String(filters.shift)],
      });
    }

    if (filters.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters.status],
      });
    }

    if (filters.fromDate && filters.toDate) {
      filterParams.push({
        field: "ShiftStartDate",
        condition: "between",
        filterValues: [
          new Date(filters.fromDate).toISOString(),
          new Date(filters.toDate).toISOString(),
        ],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleCheckboxChange = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleBulkApprove = () => {
    if (selectedRows.length === 0) return;

    showAlert({
      type: "warning",
      title: "Approve Selected Entries",
      message: `Are you sure you want to approve ${selectedRows.length} selected LHB entr${selectedRows.length > 1 ? "ies" : "y"}?`,
      showCancel: true,
      confirmText: "Yes, Approve",
      cancelText: "Cancel",
      onConfirm: () => {
        console.log("Approved Rows:", selectedRows);
        // bulk approve logic here
      },
    });
  };

  const handleBulkReject = () => {
    if (selectedRows.length === 0) return;

    showAlert({
      type: "warning",
      title: "Reject Selected Entries",
      message: `Are you sure you want to reject ${selectedRows.length} selected LHB entr${selectedRows.length > 1 ? "ies" : "y"}? Please provide a remark.`,
      showCancel: true,
      confirmText: "Yes, Reject",
      cancelText: "Cancel",
      requireInput: true,
      inputLabel: "Rejection Remark",
      onConfirm: (remark) => {
        console.log("Rejected Rows:", selectedRows, "Remark:", remark);
        // bulk reject logic here
      },
    });
  };

  // const handleSelectAll = (e) => {
  //   if (e.target.checked) {
  //     const allIds = staticLhbData.map((item) => item.id);
  //     setSelectedRows(allIds);
  //   } else {
  //     setSelectedRows([]);
  //   }
  // };

  const columns = [
    // {
    //   Header: (
    //     <input
    //       type="checkbox"
    //       checked={
    //         selectedRows.length === staticLhbData.length &&
    //         staticLhbData.length > 0
    //       }
    //       onChange={handleSelectAll}
    //     />
    //   ),
    //   accessor: "select",
    //   align: "center",
    //   width: "60px",
    //   disableSortBy: true,

    //   Cell: (cell) => (
    //     <input
    //       type="checkbox"
    //       checked={selectedRows.includes(cell.row.original.id)}
    //       onClick={(e) => e.stopPropagation()}
    //       onChange={() => handleCheckboxChange(cell.row.original.id)}
    //     />
    //   ),
    // },
    { Header: "Sr No.", accessor: "srNo", align: "left", width: "85px" },
    { Header: "Date", accessor: "date", align: "left", width: "90px" },
    { Header: "LHB No", accessor: "lhbNo", align: "left", width: "95px" },
    {
      Header: "Employee Name",
      accessor: "name",
      align: "left",
      width: "140px",
      Cell: (cell) => {
        const row = cell.row.original;
        return (
          <div
            style={{
              width: "140px",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              whiteSpace: "normal",
              lineHeight: "1.4",
            }}
          >
            {`${row.name || "-"} (${row.empNo || "-"})`}
          </div>
        );
      },
    },
    {
      Header: "Department",
      accessor: "department",
      align: "left",
      width: "120px",
      Cell: (cell) => wrapCell(cell.value, "120px"),
    },
    {
      Header: "Shift",
      accessor: "shift",
      width: "90px",
      Cell: (cell) => {
        const shift = cell.row.original.shift;
        const dutyType =
          cell.row?.original?.dutyType?.name ||
          cell.row?.original?.dutyType ||
          "-";
        return (
          <div
            style={{
              lineHeight: "1.3",
              whiteSpace: "normal",
            }}
          >
            <div>{shift || "-"}</div>
            <div>{dutyType || "-"}</div>
          </div>
        );
      },
    },
    {
      Header: "Total Hours",
      accessor: "formattedHours",
      align: "left",
      width: "119px",
    },
    {
      Header: "Action By",
      accessor: "actionBy",
      align: "left",
      width: "120px",
      Cell: (cell) => {
        const { status, approvedBy, rejectedBy, rejectionRemark } =
          cell.row.original;

        if (status === "APPROVED") {
          return (
            <div
              style={{
                width: "120px",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                lineHeight: "1.4",
              }}
            >
              {approvedBy || "-"}
            </div>
          );
        }

        if (status === "REJECTED") {
          return (
            <Box
              sx={{
                width: "120px",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                lineHeight: 1.4,
              }}
            >
              <div>{rejectedBy || "-"}</div>

              {rejectionRemark && (
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "gray",
                    marginTop: "2px",
                  }}
                >
                  {rejectionRemark}
                </div>
              )}
            </Box>
          );
        }
        return "—";
      },
    },
    {
      Header: "Status",
      accessor: "status",
      align: "left",
      width: "90px",
      Cell: (cell) => <StatusBadge status={cell.row.original.status} />,
    },
    {
      Header: "Action",
      accessor: "actions",
      align: "center",
      width: "100px",
      disableSortBy: true,

      Cell: (cell) => {
        const rowData = cell.row.original;
        const isPending = rowData.status === "PENDING";
        const encodedId = urlEncodedId(rowData.id);
        return (
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            minWidth="120px"
          >
            {/* EDIT */}
            <Tooltip
              title={isPending ? "Edit" : "Only editable when pending"}
              arrow
            >
              <span>
                <Icon
                  sx={({ palette }) => ({
                    cursor: isPending ? "pointer" : "not-allowed",
                    color: palette.secondary.main,
                    opacity: isPending ? 1 : 0.3,
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (rowData.status !== "PENDING") return;
                    navigate(`/admin/lhbData/edit/${encodedId}`);
                  }}
                >
                  <i className="fas fa-edit fa-sm" />
                </Icon>
              </span>
            </Tooltip>

            <MDTypography variant="caption">|</MDTypography>

            <Tooltip title="View" arrow>
              <Icon
                sx={({ palette }) => ({
                  cursor: isPending ? "not-allowed" : "pointer",
                  color: palette.secondary.main,
                  opacity: isPending ? 0.3 : 1,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  if (rowData.status === "PENDING") return;
                  openViewModal(rowData);
                }}
              >
                visibility
              </Icon>
            </Tooltip>
          </MDBox>
        );
      },
    },
    // {
    //   Header: "Decision",
    //   accessor: "decision",
    //   align: "center",
    //   width: "170px",
    //   Cell: (cell) => {
    //     const rowData = cell.row.original;
    //     const isPending = rowData.status === "PENDING";
    //     const isRejected = rowData.status === "REJECTED";
    //     return (
    //       <MDBox
    //         display="flex"
    //         alignItems="center"
    //         justifyContent="center"
    //         gap={1}
    //         pr={1}
    //         minWidth="160px"
    //       >
    //         {/* Reject ONLY when PENDING */}
    //         <MDButton
    //           size="small"
    //           color="error"
    //           variant="outlined"
    //           sx={{
    //             minWidth: "70px",
    //             height: "28px",
    //             visibility: isPending ? "visible" : "hidden",
    //           }}
    //           onClick={(e) => {
    //             e.stopPropagation();
    //             handleReject(rowData);
    //           }}
    //         >
    //           Reject
    //         </MDButton>

    //         {/* Approve when PENDING OR REJECTED */}
    //         <MDButton
    //           size="small"
    //           color="success"
    //           variant="outlined"
    //           sx={{
    //             minWidth: "70px",
    //             height: "28px",
    //             visibility: isPending || isRejected ? "visible" : "hidden",
    //           }}
    //           onClick={(e) => {
    //             e.stopPropagation();
    //             handleApprove(rowData);
    //           }}
    //         >
    //           Approve
    //         </MDButton>
    //       </MDBox>
    //     );
    //   },
    // },
  ];

  // const handleSearch = () => {
  //   const newfilterParams = buildFilterParams();

  //   if (
  //     JSON.stringify(filters.filterParams) !== JSON.stringify(newfilterParams)
  //   ) {
  //     setListState((prev) => ({
  //       ...prev,
  //       isLoading: true,
  //       showLoaderOnClick: true,
  //     }));

  //     setFilters((prev) => ({
  //       ...prev,
  //       filterParams: newfilterParams,
  //     }));

  //     if (pageNum !== 1) setPageNum(1);
  //   }
  // };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();

    const nextFilters = {
      ...filters,
      filterParams: newfilterParams,
    };

    if (
      JSON.stringify(filters.filterParams) !== JSON.stringify(newfilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setFilters(nextFilters);

      sessionStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({
          filters: nextFilters,
          pageNum: 1,
        }),
      );

      if (pageNum !== 1) setPageNum(1);
    }
  };

  // const handleClear = () => {
  //   setFilters({
  //     lhbNo: "",
  //     empName: "",
  //     empId: "",
  //     department: null,
  //     shift: null,
  //     status: null,
  //     fromDate: "",
  //     toDate: "",
  //     filterParams: [],
  //   });

  //   setPageNum(1);
  //   setListState((prev) => ({
  //     ...prev,
  //     isLoading: true,
  //   }));
  // };

  const handleClear = () => {
    setFilters(initialFilters);
    setPageNum(1);
    sessionStorage.removeItem(FILTER_STORAGE_KEY);

    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  LHB Records
                </MDTypography>
              </MDBox>

              <MDBox px={3} pb={2} mt={0.4} onKeyDown={handleKeyDown}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="LHB No (e.g. LHB-123 or 123)"
                      value={filters.lhbNo ? `LHB-${filters.lhbNo}` : ""}
                      onChange={(e) => {
                        let value = e.target.value.trim();
                        value = value.replace(/^LHB-/i, "");
                        value = value.replace(/\D/g, "");
                        setFilters({
                          ...filters,
                          lhbNo: value,
                        });
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by Name"
                      value={filters.empName}
                      onChange={(e) =>
                        setFilters({ ...filters, empName: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by Emp ID"
                      value={filters.empId}
                      onChange={(e) =>
                        setFilters({ ...filters, empId: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={departmentOptions.filter(
                        (d) => d.status === "ACTIVE",
                      )}
                      value={
                        departmentOptions.find(
                          (d) => d.id === filters.department,
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          department: newValue?.id || null,
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select department"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      size="small"
                      options={shiftOptions.filter(
                        (s) => s.status === "ACTIVE",
                      )}
                      value={
                        shiftOptions.find((s) => s.id === filters.shift) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                      onChange={(event, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          shift: newValue?.id || null,
                        }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Shift" />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} mt={0.2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      size="small"
                      options={STATUS_LHB_DATA}
                      getOptionLabel={(option) => option.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value?.value
                      }
                      value={
                        STATUS_LHB_DATA.find(
                          (s) => s.value === filters.status,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters((prev) => ({
                          ...prev,
                          status: newValue?.value || "",
                        }))
                      }
                      renderOption={(props, option) => (
                        <li {...props}>
                          <StatusBadge status={option.value} />
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Status" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <RequiredLabel>From Date</RequiredLabel>
                      <TextField
                        size="small"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={filters.fromDate}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            fromDate: e.target.value,
                          }))
                        }
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <RequiredLabel>To Date</RequiredLabel>
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={filters.toDate}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            toDate: e.target.value,
                          }))
                        }
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MDBox
                      display="flex"
                      gap={1}
                      flexWrap="nowrap"
                      width="100%"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          flex: 1,
                          minWidth: "90px",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {filters.filterParams.length > 0 && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            flex: 1,
                            minWidth: "90px",
                            maxWidth: "160px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox>
                {/* <MDBox
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                  mb={2}
                  mr={2}
                >
                  <MDButton
                    color="success"
                    disabled={selectedRows.length === 0}
                    onClick={() => handleBulkApprove()}
                  >
                    Approve Selected
                  </MDButton>

                  <MDButton
                    color="error"
                    disabled={selectedRows.length === 0}
                    onClick={() => handleBulkReject()}
                  >
                    Reject Selected
                  </MDButton>
                </MDBox> */}

                <MDBox
                  px={3}
                  py={1}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="body1" color="deepBlue" mb={1}>
                    Total Records: {timeSheetDataList?.data?.totalRecords || 0}
                  </MDTypography>
                </MDBox>

                <MDBox sx={{ overflowX: "auto" }}>
                  {/* <MDBox
                    sx={{
                      height: "500px",
                      overflow: "auto",

                      "& .MuiTableContainer-root": {
                        maxHeight: "500px",
                        overflow: "auto",
                      },

                      "& .MuiTable-root": {
                        minWidth: "1700px",
                      },

                      "& thead th": {
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 10,
                      },
                    }}
                  >  */}
                  {listState.isLoading ? (
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="150px"
                    >
                      <i className="fa fa-spinner fa-spin fa-2xl loader" />
                    </MDBox>
                  ) : listState.error ? (
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="150px"
                    >
                      <MDTypography color="error" variant="body2">
                        {listState.error}
                      </MDTypography>
                    </MDBox>
                  ) : listState.noRecords ? (
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="20vh"
                    >
                      <MDTypography color="text" variant="body2">
                        No timesheet added yet.
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <DataTable
                      columns={columns}
                      data={formattedListData || []}
                      isSorted
                      pageNum={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        (timeSheetDataList?.data?.totalRecords || 0) /
                          (timeSheetDataList?.data?.noOfRecordsPerPage ||
                            DEFAULT_RECORDS_PER_PAGE),
                      )}
                      // onRowClick={(row) => openViewModal(row)}
                    />
                  )}

                  {/* </MDBox> */}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default LHBDataListing;
